import { SYSTEM_ROLES, type SystemRoleCode, UI_MODES, type Permission, type UiMode } from '@batixa/domain';
import {
  BarChart3,
  Bot,
  ClipboardList,
  FileText,
  FolderKanban,
  Home,
  Library,
  LogOut,
  Menu,
  Plus,
  Receipt,
  Ruler,
  Settings,
  UserPlus,
  Users,
  X,
  type LucideIcon, ShieldCheck, HelpCircle, LayoutTemplate } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { WelcomeModal } from '../components/WelcomeModal';
import { SUPPORT } from '../lib/contact';
import { Logo } from '../components/Logo';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: Permission;
  /** Modes où l'entrée apparaît ; absent = tous. Les pages restent accessibles : seul l'affichage change. */
  modes?: UiMode[];
  /** Autres pages qui rattachent cette entrée (mise en évidence dans le menu). */
  also?: string[];
}

const PRO: UiMode[] = ['PRO', 'EXPERT'];

export const NAV: NavItem[] = [
  { to: '/', label: 'Accueil', icon: Home },
  { to: '/projets', label: 'Mes projets', icon: FolderKanban, permission: 'project:read', modes: PRO, also: ['/import', '/plans'] },
  { to: '/metre', label: 'Métré', icon: Ruler, permission: 'project:read', modes: PRO },
  { to: '/modeles', label: 'Modèles de devis', icon: LayoutTemplate, permission: 'project:read' },
  { to: '/devis', label: 'Devis', icon: ClipboardList, permission: 'quotation:read' },
  { to: '/factures', label: 'Factures', icon: Receipt, permission: 'invoice:read' },
  { to: '/bibliotheque', label: 'Bibliothèque BTP', icon: Library, permission: 'catalog:read', modes: PRO, also: ['/materiaux', '/materiels', '/main-oeuvre'] },
  { to: '/clients', label: 'Clients', icon: Users, permission: 'project:read' },
  { to: '/ai', label: 'ExpertDevis AI', icon: Bot, permission: 'ai:use' },
  { to: '/rapports', label: 'Rapports', icon: BarChart3, permission: 'quotation:read', modes: PRO, also: ['/analyse'] },
  { to: '/parametres', label: 'Paramètres', icon: Settings },
  { to: '/aide', label: 'Aide', icon: HelpCircle },
];

export const MODE_LABELS: Record<UiMode, string> = { SIMPLE: 'Simple', PRO: 'Professionnel', EXPERT: 'Expert' };

/** Actions rapides : mêmes destinations dans le menu mobile et sur l'accueil. */
export function useShortcuts() {
  const { can, user } = useAuth();
  const simple = user?.uiMode === 'SIMPLE';
  const all: Array<{ to: string; label: string; icon: LucideIcon; show: boolean }> = [
    { to: '/projets?nouveau=1', label: 'Nouveau projet', icon: FolderKanban, show: !simple && can('project:create') },
    { to: '/devis/rapide', label: 'Nouveau devis', icon: FileText, show: can('project:create') && can('project:edit') && can('quotation:edit') },
    { to: '/factures/nouvelle', label: 'Nouvelle facture', icon: Receipt, show: can('invoice:edit') },
    { to: '/metre', label: 'Nouveau métré', icon: Ruler, show: !simple && can('project:read') },
    { to: '/clients?nouveau=1', label: 'Nouveau client', icon: UserPlus, show: can('project:edit') },
    { to: '/ai', label: 'Assistant IA', icon: Bot, show: can('ai:use') },
  ];
  return all.filter((s) => s.show);
}

export function AppShell() {
  const { user, logout, can, setUiMode } = useAuth();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const { pathname } = useLocation();
  const shortcuts = useShortcuts();
  const mode = user?.uiMode ?? 'PRO';
  const items = [...NAV.filter((i) => (!i.permission || can(i.permission)) && (!i.modes || i.modes.includes(mode))), ...(user?.platformAdmin ? [{ to: '/plateforme', label: 'Plateforme', icon: ShieldCheck }] : [])];
  const simple = mode === 'SIMPLE';
  const main = shortcuts[0];
  const isActive = (item: NavItem) => (item.to === '/' ? pathname === '/' : [item.to, ...(item.also ?? [])].some((p) => pathname === p || pathname.startsWith(`${p}/`)));
  const second = items.find((i) => i.to === (simple ? '/devis' : '/projets'));
  const third = items.find((i) => i.to === '/ai') ?? items.find((i) => i.to === '/clients');

  return (
    <div className="flex min-h-screen">
      {open && <div className="fixed inset-0 z-40 bg-navy-950/50 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-line bg-white transition-transform lg:static lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-start justify-between border-b border-line px-4 py-3">
          <Link to="/" onClick={() => setOpen(false)} aria-label="Accueil">
            <Logo className="h-auto w-full max-w-52" />
          </Link>
          <button className="btn-ghost -mr-2 px-2 lg:hidden" onClick={() => setOpen(false)} aria-label="Fermer le menu">
            <X size={18} />
          </button>
        </div>
        {main && (
          <div className="px-3 pt-4">
            <Link to={main.to} onClick={() => setOpen(false)} className="btn-accent w-full uppercase">
              <Plus size={16} /> {main.label}
            </Link>
          </div>
        )}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                isActive(item) ? 'bg-tech-50 text-navy-900 shadow-[inset_3px_0_0_var(--color-brand-500)]' : 'text-muted hover:bg-surface hover:text-navy-900'
              }`}
            >
              <item.icon size={18} aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-line px-4 py-3 text-xs text-muted">Le chiffrage intelligent de tous vos projets BTP.</div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-line bg-white/95 px-4 backdrop-blur lg:h-16 lg:px-8">
          <div className="min-w-0 truncate text-sm font-medium text-navy-900">{user?.companyName}</div>
          <div className="flex shrink-0 items-center gap-2 lg:gap-3">
            <label className="hidden items-center gap-2 text-xs text-muted sm:flex">
              Mode
              <select className="input w-auto py-1 text-xs" value={mode} onChange={(e) => void setUiMode(e.target.value as UiMode)} aria-label="Mode d'utilisation">
                {UI_MODES.map((m) => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
              </select>
            </label>
            <div className="hidden text-right leading-tight md:block">
              <div className="text-sm font-semibold">{user?.fullName}</div>
              <div className="text-xs text-muted">{user?.roles.map((r) => SYSTEM_ROLES[r as SystemRoleCode]?.name ?? 'Rôle personnalisé').join(' · ')}</div>
            </div>
            <Link to={`/aide?depuis=${encodeURIComponent(pathname)}`} className="btn-secondary px-2.5" aria-label="Aide sur cet écran" title="Aide sur cet écran">
              <HelpCircle size={16} />
            </Link>
            <button className="btn-secondary px-2.5 lg:px-4" onClick={() => void logout()} aria-label="Déconnexion">
              <LogOut size={16} />
              <span className="hidden lg:inline">Déconnexion</span>
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 pb-24 lg:px-8 lg:pb-6">
          {user?.readOnly && (
            <div className="mb-4 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-sm" role="status">
              <strong>Abonnement suspendu : lecture seule.</strong> Vos données restent consultables et exportables, aucune modification n'est possible. Contactez l'équipe ExpertDevis BTP : {SUPPORT.email} ou WhatsApp {SUPPORT.phone}.
            </div>
          )}
          <WelcomeModal />
          <Outlet />
        </main>
      </div>

      {/* Navigation compacte sur téléphone. */}
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-line bg-white lg:hidden" aria-label="Navigation principale">
        {[items[0], second].map((i) => i && <BarItem key={i.to} to={i.to} label={i.label} icon={i.icon} active={isActive(i)} />)}
        <button className="flex flex-col items-center justify-center py-1.5" onClick={() => setSheet(true)} aria-label="Actions rapides">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-white"><Plus size={22} /></span>
        </button>
        {third && <BarItem to={third.to} label={third.to === '/ai' ? 'IA' : third.label} icon={third.icon} active={isActive(third)} />}
        <button className="flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] text-muted" onClick={() => setOpen(true)}>
          <Menu size={20} /> Menu
        </button>
      </nav>

      {sheet && (
        <div className="fixed inset-0 z-50 flex items-end bg-navy-950/50 lg:hidden" onClick={() => setSheet(false)}>
          <div className="w-full rounded-t-2xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-semibold text-navy-900">Actions rapides</span>
              <button className="btn-ghost px-2" onClick={() => setSheet(false)} aria-label="Fermer"><X size={18} /></button>
            </div>
            <div className="grid gap-1">
              {shortcuts.map((s) => (
                <Link key={s.to} to={s.to} onClick={() => setSheet(false)} className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium hover:bg-surface">
                  <s.icon size={18} className="text-brand-600" /> {s.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BarItem({ to, label, icon: Icon, active }: { to: string; label: string; icon: LucideIcon; active: boolean }) {
  return (
    <Link to={to} className={`flex min-w-0 flex-col items-center justify-center gap-0.5 py-2 text-[11px] ${active ? 'font-semibold text-navy-900' : 'text-muted'}`}>
      <Icon size={20} aria-hidden="true" />
      <span className="max-w-full truncate px-1">{label}</span>
    </Link>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-navy-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}
